import {
  ExternalIntegrationEnum,
  ExternalIntegrationsClient,
  GoHighLevelIntegration,
  GoogleAdsIntegration,
  MailchimpIntegration,
  WixIntegration,
  ShopifyIntegration,
} from 'api/clients/ExternalIntegrationsClient'
import { ConnectableAccount, IntegrationAuthsClient } from 'api/clients/IntegrationAuthsClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useOauthQueryParams } from 'hooks/useOauthQueryParams'
import { useOnboardingTracking } from 'hooks/useOnboardingTracking'
import { useMutation, useQuery, UseQueryOptions } from 'react-query'
import { useModals } from 'almanac-ui/Modal'
import { GoHighLevelConnectedSuccessModal } from 'components/GoHighLevelConnectedSuccessModal/GoHighLevelConnectedSuccessModal'
import { GoogleAdsConversionTrackingModal } from 'components/GoogleAdsConversionTrackingModal/GoogleAdsConversionTrackingModal'
import { GoogleAdsExistingCampaignsModal } from 'components/GoogleAdsExistingCampaignsModal/GoogleAdsExistingCampaignsModal'
import { IntegrationSuccessModal } from 'components/IntegrationSuccessModal/IntegrationSuccessModal'
import { MailchimpConnectedSuccessModal } from 'components/MailchimpConnectedSuccessModal/MailchimpConnectedSuccessModal'
import { WixConnectedSuccessModal } from 'components/WixConnectedSuccessModal/WixConnectedSuccessModal'
import { ShopifyConnectedSuccessModal } from 'components/ShopifyConnectedSuccessModal/ShopifyConnectedSuccessModal'
import type { Response } from 'request-dot-js'
import { useAuth } from 'hooks/UseAuth'
import { USERS, checkFeatureFlagUserGroup } from 'utils/featureFlagUserGroups'

const extractOauthParams = (provider: ExternalIntegrationEnum, queryParams: Record<string, string>) => {
  switch (provider) {
    case ExternalIntegrationEnum.Facebook:
      return {
        code: queryParams.code,
        error: queryParams.error,
        state: queryParams.state,
        scopes: queryParams.granted_scopes,
      }
    case ExternalIntegrationEnum.LinkedIn:
      return {
        code: queryParams.code,
        error: queryParams.error,
        state: queryParams.state,
        // We hardcode the scopes here because LinkedIn doesn't return them in the query params
        scopes: [
          'w_member_social',
          'r_basicprofile',
          'w_organization_social',
          'rw_organization_admin',
          'r_organization_social',
        ],
      }
    case ExternalIntegrationEnum.Instagram:
      return {
        access_token: queryParams.long_lived_token || queryParams['#access_token'],
        error: queryParams.error,
        state: queryParams.state,
        scopes: queryParams.granted_scopes,
      }
    case ExternalIntegrationEnum.X:
      return {
        error: queryParams.denied,
        oauth_token: queryParams.oauth_token,
        oauth_verifier: queryParams.oauth_verifier,
        oauth_token_secret: localStorage.getItem('x_oauth_token_secret'),
        state: queryParams.state,
      }
    case ExternalIntegrationEnum.TikTok:
      // Todo: Remove once TikTok metrics are fully enabled
      const scopes = [
        'user.info.basic',
        'video.publish',
        'video.upload',
        'video.list',
        'user.info.profile',
        'user.info.stats',
      ]
      return {
        code: queryParams.code,
        error: queryParams.error,
        state: queryParams.state,
        error_description: queryParams.error_description,
        scopes,
      }
    case ExternalIntegrationEnum.YouTube:
      return {
        code: queryParams.code,
        error: queryParams.error,
        state: queryParams.state,
        scope: queryParams.scope,
      }
    case ExternalIntegrationEnum.Google:
      return {
        code: queryParams.code,
        error: queryParams.error,
        state: queryParams.state,
        scope: queryParams.scope,
      }
    case ExternalIntegrationEnum.GoogleDrive:
      return {
        code: queryParams.code,
        error: queryParams.error,
        state: queryParams.state,
        scope: queryParams.scope,
      }
    case ExternalIntegrationEnum.GoogleBusinessProfile:
      return {
        code: queryParams.code,
        error: queryParams.error,
        state: queryParams.state,
        scope: queryParams.scope,
      }
    case ExternalIntegrationEnum.Mailchimp:
      return {
        code: queryParams.code,
        error: queryParams.error,
        state: queryParams.state,
      }
    case ExternalIntegrationEnum.MetaAds:
      return {
        code: queryParams.code,
        error: queryParams.error,
        state: queryParams.state,
        scopes: queryParams.granted_scopes,
      }
    case ExternalIntegrationEnum.GoogleAds:
      return {
        code: queryParams.code,
        error: queryParams.error,
        state: queryParams.state,
        scope: queryParams.scope,
      }
    case ExternalIntegrationEnum.GoHighLevel:
      return {
        code: queryParams.code,
        error: queryParams.error,
        state: queryParams.state,
      }
    case ExternalIntegrationEnum.Wix:
      return {
        code: queryParams.code,
        error: queryParams.error,
        error_description: queryParams.error_description,
        state: queryParams.state,
        instanceId: queryParams.instanceId,
        signedInstance: queryParams.signedInstance,
        tenantId: queryParams.tenantId,
      }
    case ExternalIntegrationEnum.Shopify:
      return {
        code: queryParams.code,
        error: queryParams.error,
        state: queryParams.state,
        shop: queryParams.shop,
        hmac: queryParams.hmac,
        host: queryParams.host,
        timestamp: queryParams.timestamp,
      }
    default:
      return null
  }
}

export const useOauthRedirectParams = (provider: ExternalIntegrationEnum) => {
  const { ...params } = useOauthQueryParams()
  const extractedParams = extractOauthParams(provider as ExternalIntegrationEnum, params)

  return extractedParams
}

type Data = { connectableAccounts: ConnectableAccount[]; metadata: any }

export interface Options extends UseQueryOptions<Data> {}

interface Props {
  integrationAuthId?: number
}

export const useConnectableAccounts = ({ integrationAuthId }: Props, options?: Options) => {
  const workspaceId = useCurrentWorkspaceId()
  return useQuery<Data>(
    ['connectableAccounts', integrationAuthId],
    async () => {
      if (!integrationAuthId) {
        throw Error('No integration auth id provided')
      }

      const res = await IntegrationAuthsClient.connectableAccounts({ integrationAuthId, workspaceId })

      if (res.type === SUCCESS_RESPONSE_TYPE) {
        return {
          connectableAccounts: res.data.data,
          metadata: res.data.metadata,
        }
      } else {
        showToastMessage('Failed to fetch connectable accounts', 'error')
        throw new Error('Failed to fetch connectable accounts')
      }
    },
    {
      enabled: !!integrationAuthId,
      ...options,
    },
  )
}

export const useCreateExternalIntegration = () => {
  const { trackAccountConnected } = useOnboardingTracking()

  return useMutation(ExternalIntegrationsClient.createExternalIntegration, {
    onSuccess: (res) => {
      if (res.type === 'success') {
        trackAccountConnected(res.data.external_integration.provider)
      }
    },
  })
}

/**
 * Hook to show the appropriate success modal based on the provider and result.
 * Returns a function that shows the modal and returns true if a modal was shown, false if it should just redirect.
 */
export const useShowIntegrationSuccessModal = () => {
  const { openModal } = useModals()
  const auth = useAuth()
  const isAlmanacUser = checkFeatureFlagUserGroup([USERS.withAlmanacEmail], auth?.email)

  return ({
    provider,
    result,
    onClose,
  }: {
    provider: ExternalIntegrationEnum
    result: Response<{ external_integration: any }, any> | undefined
    onClose: () => void
  }): boolean => {
    if (!result || result.type !== SUCCESS_RESPONSE_TYPE || !result.data) {
      return false
    }

    const integration = result.data.external_integration

    if (provider === ExternalIntegrationEnum.Mailchimp) {
      openModal(
        MailchimpConnectedSuccessModal,
        {
          integration: integration as MailchimpIntegration,
          onClose,
        },
        { newStack: true },
      )
      return true
    }

    if (provider === ExternalIntegrationEnum.GoHighLevel) {
      openModal(
        GoHighLevelConnectedSuccessModal,
        {
          integration: integration as GoHighLevelIntegration,
          onClose,
        },
        { newStack: true },
      )
      return true
    }

    if (provider === ExternalIntegrationEnum.Wix) {
      openModal(
        WixConnectedSuccessModal,
        {
          integration: integration as WixIntegration,
          onClose,
        },
        { newStack: true },
      )
      return true
    }

    if (provider === ExternalIntegrationEnum.Shopify) {
      openModal(
        ShopifyConnectedSuccessModal,
        {
          integration: integration as ShopifyIntegration,
          onClose,
        },
        { newStack: true },
      )
      return true
    }

    // For Google, GoogleDrive, MetaAds, don't show a modal, just redirect
    if (
      provider === ExternalIntegrationEnum.Google ||
      provider === ExternalIntegrationEnum.GoogleDrive ||
      provider === ExternalIntegrationEnum.GoogleBusinessProfile ||
      provider === ExternalIntegrationEnum.MetaAds
    ) {
      return false
    }

    if (provider === ExternalIntegrationEnum.GoogleAds) {
      const googleAdsIntegration = integration as GoogleAdsIntegration

      if (googleAdsIntegration.additional_data?.conversion_tracking_confirmed === false && !isAlmanacUser) {
        openModal(GoogleAdsConversionTrackingModal, { integration: googleAdsIntegration, onClose }, { newStack: true })
        return true
      }

      if ((googleAdsIntegration.additional_data?.existing_campaigns_summary?.length ?? 0) > 0) {
        openModal(GoogleAdsExistingCampaignsModal, { integration: googleAdsIntegration, onClose }, { newStack: true })
        return true
      }

      return false
    }

    // For all other providers, show the generic success modal
    openModal(
      IntegrationSuccessModal,
      {
        integration,
        onClose,
      },
      { newStack: true },
    )
    return true
  }
}
